// src/pages/Dashboard.js
// Apple Dark Mode Style
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Dashboard.css';
import API_URL from '../config';

function Dashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const userPlan = localStorage.getItem('userPlan') || 'free';

  // Plan limits
  const GROUP_LIMITS = {
    free: 2,
    plus: 5,
    premium: 20,
    pro: 100
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchGroups = async () => {
      try {
        const response = await fetch(`${API_URL}/api/groups/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const groupsWithData = await response.json();
          setGroups(groupsWithData);
        } else if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [token, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return t('dashboard.notScheduled');
    
    const date = new Date(dateString);
    const today = new Date();
    const diffDays = Math.floor((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return t('dashboard.today');
    if (diffDays === 1) return t('dashboard.tomorrow');
    
    const locale = i18n.language === 'en' ? 'en-US' : 'sv-SE';
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const getWeekdayDisplay = (dayOfWeek) => {
    if (!dayOfWeek) return '';
    if (i18n.language === 'sv') {
      return `${dayOfWeek}ar`;
    }
    return `${dayOfWeek}s`;
  };

  const handleCreateGroup = async () => {
    const currentLimit = GROUP_LIMITS[userPlan];
    
    if (currentLimit && groups.length >= currentLimit) {
      navigate('/priser');
      return;
    }

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          groupName: t('nav.newGroupDefault'),
          meetingFrequency: { dayOfWeek: '', time: '' },
          threshold: null,
          timezone: userTimezone,
          location: '',
          invitationAdvance: 7,
          reminderAdvance: 3,
          decisionTime: '18:00'
        })
      });

      if (response.ok) {
        const newGroup = await response.json();
        window.dispatchEvent(new Event('groupsUpdated'));
        navigate(`/groups/${newGroup._id}`);
      }
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  // Skeleton Loading
  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>{t('dashboard.title')}</h1>
          <p className="subtitle">{t('dashboard.subtitle')}</p>
        </div>
        
        <div className="groups-grid">
          {[1, 2].map(i => (
            <div key={i} className="group-card skeleton-card">
              <div className="skeleton-header">
                <div className="skeleton-title"></div>
                <div className="skeleton-location"></div>
              </div>
              <div className="skeleton-next-meeting"></div>
              <div className="skeleton-stats">
                <div className="skeleton-stat"></div>
                <div className="skeleton-stat"></div>
                <div className="skeleton-stat"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty State
  if (groups.length === 0) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>{t('dashboard.title')}</h1>
          <p className="subtitle">{t('dashboard.subtitle')}</p>
        </div>

        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <h2>{t('dashboard.empty.title')}</h2>
          <p>{t('dashboard.empty.description')}</p>
          <button className="btn-create" onClick={handleCreateGroup}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {t('dashboard.empty.createButton')}
          </button>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>{t('dashboard.title')}</h1>
        <p className="subtitle">{t('dashboard.subtitle')}</p>
      </div>

      <div className="groups-grid">
        {groups.map(group => (
          <div 
            key={group._id} 
            className="group-card"
            onClick={() => navigate(`/groups/${group._id}`)}
          >
            {/* Status Badge */}
            {group.status === 'draft' && (
              <span className="status-badge draft">
                {t('dashboard.draft')}
              </span>
            )}

            <div className="card-header">
              <h2>
                {group.groupName}
                {group.meetingFrequency?.dayOfWeek && (
                  <span className="meeting-day">
                    – {getWeekdayDisplay(group.meetingFrequency.dayOfWeek)}
                  </span>
                )}
              </h2>
              {group.location && (
                <div className="location">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {group.location}
                </div>
              )}
            </div>

            {group.status?.nextMeeting ? (
              <>
                <div className="next-meeting">
                  <strong>{t('dashboard.nextMeeting')}</strong> {formatDate(group.status.nextMeeting)}
                </div>
                
                {/* Response Stats */}
                <div className="responses-summary">
                  <div className="response-stat">
                    <span className="icon">✓</span>
                    <strong>{group.status.responses?.yes || 0}</strong>
                    <small>{t('dashboard.yes')}</small>
                  </div>
                  <div className="response-stat">
                    <span className="icon">✕</span>
                    <strong>{group.status.responses?.no || 0}</strong>
                    <small>{t('dashboard.no')}</small>
                  </div>
                  <div className="response-stat">
                    <span className="icon">…</span>
                    <strong>{group.status.responses?.pending || 0}</strong>
                    <small>{t('dashboard.waiting')}</small>
                  </div>
                </div>

                {/* Who's Coming */}
                {group.participantsStatus?.participants?.coming?.length > 0 && (
                  <div className="participants-coming">
                    <div className="coming-header">
                      {t('dashboard.coming')}:
                    </div>
                    <div className="coming-list">
                      {group.participantsStatus.participants.coming.slice(0, 5).map((p, index) => (
                        <React.Fragment key={p.id}>
                          <span className="participant-name">{p.name}</span>
                          {index < Math.min(4, group.participantsStatus.participants.coming.length - 1) && ', '}
                        </React.Fragment>
                      ))}
                      {group.participantsStatus.participants.coming.length > 5 && (
                        <span className="more-count">
                          {' '}+{group.participantsStatus.participants.coming.length - 5} {t('dashboard.more')}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Warning if not reaching threshold */}
                {!group.status?.reachesThreshold && group.threshold && (
                  <div className="warning-box">
                    ⚠️ {t('dashboard.needsMoreResponses', {
                      count: group.threshold - (group.status?.responses?.yes || 0),
                      defaultValue: `Behöver ${group.threshold - (group.status?.responses?.yes || 0)} till för att nå tröskeln`
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="no-status">
                <strong>{t('dashboard.groupIncomplete')}</strong>
                {t('dashboard.addMeetingInfo')}
              </div>
            )}

            <button 
              className="btn-details" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/groups/${group._id}`);
              }}
            >
              {t('dashboard.seeDetails')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        ))}

        {/* Create New Group Card */}
        {groups.length < (GROUP_LIMITS[userPlan] || 100) && (
          <div 
            className="group-card"
            onClick={handleCreateGroup}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '200px',
              background: 'var(--bg-tertiary)',
              border: '2px dashed var(--glass-border)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                margin: '0 auto var(--space-md)',
                background: 'var(--accent-subtle)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
              <p style={{ 
                color: 'var(--text-secondary)', 
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-medium)',
                margin: 0
              }}>
                {t('dashboard.createNew')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
